import { useState, useEffect } from 'react';
import { MilestoneMap1, MilestoneMapInterval } from '../../types';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { createMilestoneMap } from '../../store/map3PendingSlice';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../components_auth/AuthContext';
import { Description, Field, Label } from '../../components_catalyst/fieldset';
import { getShortIdFromUuid } from '@mm-shared/utils/getShortIdFromUuid';
import { SaveAndRefresh } from '../../components_misc/SaveAndRefresh';
import { getIntervalsForMilestoneMap } from '../../utils/getIntervalsForMilestoneMap';

// Define an interface for your form data
interface FormData extends Partial<MilestoneMap1> {
  title: string;
  interval: MilestoneMapInterval;
  intervalInDays: number;
}

export function AddMilestoneMap1() {
  const { user } = useAuth();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const userProfile = useSelector((state: RootState) => state.map3Data.userProfile);

  const [isDebugMode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('debug') === 'true';
  });

  const [uuid, setUuid] = useState(() => crypto.randomUUID());
  const [shortId, setShortId] = useState(() => getShortIdFromUuid(uuid));
  const [formData, setFormData] = useState<Partial<MilestoneMap1>>({
    title: '',
    interval: 'weeks',
    intervalInDays: 7,
    startDate: '',
    endDate: '',
    createdBy: userProfile?.userName || '',
    owner: {
      uid: userProfile.uid,
      name: userProfile.userName,
      entityType: 'user',
    },
  });

  const [validationError, setValidationError] = useState<string | null>(null);

  const calculateIntervals = (start: string, end: string, interval: MilestoneMapInterval) => {
    // Create a minimal MilestoneMap1 object for interval calculation
    const map = {
      startDate: start,
      endDate: end,
      interval,
    } as any; // Only the needed fields
    return getIntervalsForMilestoneMap(map).length;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Add authentication check
    if (!user) {
      setValidationError('You must be signed in to create a milestone map');
      return;
    }

    // Sanitize inputs
    const sanitizedTitle = formData.title?.trim().slice(0, 200);

    if (!sanitizedTitle) {
      setValidationError('Title is required');
      return;
    }

    // Ensure end date is after start date
    const endDate = new Date(formData.endDate || '');
    const startDate = new Date(formData.startDate || '');
    if (endDate <= startDate) {
      setValidationError('End date must be after start date');
      return;
    }

    // Ensure we have both dates
    if (!formData.startDate || !formData.endDate) {
      setValidationError('Please select both start and end dates');
      return;
    }

    // Calculate number of intervals
    const intervals = calculateIntervals(
      formData.startDate,
      formData.endDate,
      formData.interval as MilestoneMapInterval,
    );

    // Show warning but don't prevent submission if intervals < 13
    if (intervals < 10) {
      setValidationError(
        `Milestone maps must have at least 10 intervals (columns on the map). With the dates you selected below, yours has ${intervals} intervals. Support for other sizes comming soon. For now, please set your dates farther apart or select a shorter interval.`,
      );
      return;
    }

    // Validate maximum intervals to prevent DoS
    if (intervals > 365) {
      setValidationError('Milestone map cannot exceed 365 intervals');
      return;
    }

    setValidationError(null);

    try {
      const newMap: MilestoneMap1 = {
        ...formData,
        title: sanitizedTitle,
        uuid,
        shortId,
        objectiveUuids: [],
        parentObjectiveUuids: [],
        createdDate: new Date().toISOString(),
        createdBy: userProfile.userName,
        owner: {
          uid: user.uid,
          name: userProfile.userName,
          entityType: 'user',
        },
        featureFlags: {
          enableRelatives: false,
          enablePlaylists: false,
          enableCrosshairs: false,
          enableDependencies: false,
          enableAddExistingObjectives: false,
        },
      } as MilestoneMap1;

      // Wait for the dispatch to complete and handle any errors
      const resultAction = await dispatch(createMilestoneMap(newMap));

      if (createMilestoneMap.rejected.match(resultAction)) {
        throw new Error(resultAction.error.message || 'Failed to create milestone map');
      }

      // Use the returned map's shortId if available
      const createdMap = resultAction.payload;
      navigate(`/${userProfile.userName}/maps/${createdMap?.shortId || shortId}`);
    } catch (error) {
      console.error('Error creating milestone map:', error);
      setValidationError(
        error instanceof Error
          ? error.message
          : 'Failed to create milestone map. Please try again.',
      );
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === 'title') {
      const sanitizedValue = value.replace(/[<>]/g, '');
      setFormData((prev: Partial<MilestoneMap1>) => ({ ...prev, [name]: sanitizedValue }));
      return;
    }

    if (name === 'interval') {
      const allowedIntervals = ['hours', 'days', 'weeks', 'months', 'quarters', 'years'];
      if (!allowedIntervals.includes(value)) {
        return;
      }
      setFormData((prev: Partial<MilestoneMap1>) => ({
        ...prev,
        interval: value as 'hours' | 'days' | 'weeks' | 'months' | 'quarters' | 'years',
      }));
    } else {
      setFormData((prev: Partial<MilestoneMap1>) => ({ ...prev, [name]: value }));
    }
  };

  function getInputClassName(
    fieldName:
      | 'title'
      | 'interval'
      | 'intervalInDays'
      | 'startDate'
      | 'endDate'
      | 'uuid'
      | 'shortId'
      | 'createdDate'
      | 'createdBy'
      | 'owner',
  ): string {
    const baseClass =
      'block w-full h-[38px] rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6';
    return `${baseClass} ${validationError ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300'}`;
  }

  useEffect(() => {
    if (!formData.startDate || !formData.interval) return;

    // Only auto-update end date if it's empty
    if (!formData.endDate) {
      // Calculate a default end date 13 intervals after start date
      let start = new Date(formData.startDate);
      let end = new Date(start);

      switch (formData.interval) {
        case 'hours':
          end.setHours(start.getHours() + 12);
          break;
        case 'days':
          end.setDate(start.getDate() + 12);
          break;
        case 'weeks':
          end.setDate(start.getDate() + 7 * 12);
          break;
        case 'months':
          end.setMonth(start.getMonth() + 12);
          break;
        case 'quarters':
          end.setMonth(start.getMonth() + 3 * 12);
          break;
        case 'years':
          end.setFullYear(start.getFullYear() + 12);
          break;
        default:
          break;
      }

      setFormData((prev) => ({
        ...prev,
        endDate: end.toISOString().split('T')[0],
      }));
    }
  }, [formData.startDate, formData.interval]);

  return (
    <main
      className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8"
      role="main"
      aria-labelledby="page-title"
    >
      <SaveAndRefresh hidden />
      <h1 id="page-title" className="text-2xl font-bold mb-6">
        Create New Milestone Map
      </h1>

      {validationError && (
        <div className="rounded-md bg-red-50 p-4 mb-6" role="alert" aria-live="polite">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{validationError}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6" aria-label="Create milestone map form">
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
          <label
            htmlFor="title"
            className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5"
          >
            Title *
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className={getInputClassName('title')}
              aria-label="Map title"
              aria-required="true"
              placeholder="Enter map title"
            />
          </div>
        </div>

        <Field className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
          <label
            htmlFor="interval"
            className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5"
          >
            Interval *
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <select
              id="interval"
              name="interval"
              value={formData.interval}
              onChange={handleChange}
              required
              className={getInputClassName('interval')}
              aria-label="Time interval"
              aria-required="true"
            >
              {/* <option value="hours">Hourly</option> */}
              <option value="days">Daily</option>
              <option value="weeks">Weekly</option>
              <option value="months">Monthly</option>
              <option value="quarters">Quarterly</option>
              <option value="years">Yearly</option>
            </select>
            <Description>
              What unit of time do you want to represent with a column in this map? Tip: If you plan
              to hold status check-ins or oversight meetings weekly, select weekly. If you will meet
              to review and update status monthly, select monthly, etc.
            </Description>
          </div>
        </Field>

        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
          <label
            htmlFor="startDate"
            className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5"
          >
            Start Date *
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <input
              type="date"
              id="startDate"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              required
              className={getInputClassName('startDate')}
              aria-label="Start date"
              aria-required="true"
            />
          </div>
        </div>

        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
          <label
            htmlFor="endDate"
            className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5"
          >
            End Date *
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <input
              type="date"
              id="endDate"
              name="endDate"
              value={formData.endDate}
              onChange={handleChange}
              required
              className={getInputClassName('endDate')}
              aria-label="End date"
              aria-required="true"
            />
          </div>
        </div>

        {/* Debug mode fields */}
        {isDebugMode && (
          <section className="space-y-6" role="region" aria-label="Debug options">
            <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
              <label
                htmlFor="uuid"
                className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5"
              >
                UUID
              </label>
              <div className="mt-2 sm:col-span-2 sm:mt-0">
                <input
                  type="text"
                  id="uuid"
                  name="uuid"
                  value={uuid}
                  onChange={handleChange}
                  className={getInputClassName('uuid')}
                  aria-label="UUID"
                />
              </div>
            </div>

            <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
              <label
                htmlFor="shortId"
                className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5"
              >
                Short ID
              </label>
              <div className="mt-2 sm:col-span-2 sm:mt-0">
                <input
                  type="text"
                  id="shortId"
                  name="shortId"
                  value={shortId}
                  onChange={handleChange}
                  disabled={!isDebugMode}
                  className={getInputClassName('shortId')}
                  aria-label="Short ID"
                />
              </div>
            </div>

            <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
              <label
                htmlFor="createdDate"
                className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5"
              >
                Created Date
              </label>
              <div className="mt-2 sm:col-span-2 sm:mt-0">
                <input
                  type="date"
                  id="createdDate"
                  name="createdDate"
                  value={new Date().toISOString().split('T')[0]}
                  onChange={handleChange}
                  disabled={!isDebugMode}
                  className={getInputClassName('createdDate')}
                  aria-label="Created date"
                />
              </div>
            </div>

            <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
              <label
                htmlFor="createdBy"
                className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5"
              >
                Created By
              </label>
              <div className="mt-2 sm:col-span-2 sm:mt-0">
                <input
                  type="text"
                  id="createdBy"
                  name="createdBy"
                  value={formData.createdBy}
                  onChange={handleChange}
                  disabled={!isDebugMode}
                  className={getInputClassName('createdBy')}
                  aria-label="Created by"
                />
              </div>
            </div>
          </section>
        )}

        <Field className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
          <Label htmlFor="owner" className="sm:pt-1.5">
            Owner
          </Label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <select
              id="owner"
              name="owner"
              value={userProfile?.userName || ''}
              disabled={true}
              className={getInputClassName('owner')}
              aria-label="Map owner"
            >
              <option value={userProfile?.userName || ''}>
                {userProfile?.userName || 'Loading...'}
              </option>
            </select>
            <Description>
              Currently, new maps can only be owned by you. Organization ownership will be available
              soon.
            </Description>
          </div>
        </Field>

        <div className="text-sm text-gray-500">Fields marked with * are required</div>

        <div className="mt-6 flex items-center justify-end gap-x-6">
          <button
            type="submit"
            className="h-[38px] rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
            aria-label="Create milestone map"
          >
            Create Milestone Map
          </button>
        </div>
      </form>
    </main>
  );
}
